import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, NavigationEnd, RouterModule } from '@angular/router';
import { filter, distinctUntilChanged } from 'rxjs/operators';
import { MatIconModule } from '@angular/material/icon';

interface Breadcrumb {
  label: string;
  url: string;
}

@Component({
  selector: 'app-breadcrumb',
  templateUrl: './breadcrumb.component.html',
  styleUrls: ['./breadcrumb.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatIconModule
  ]
})
export class BreadcrumbComponent implements OnInit {
  breadcrumbs: Breadcrumb[] = [];

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {}

  ngOnInit() {
    // Initialize breadcrumbs for the current route
    this.initializeBreadcrumbs();

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      distinctUntilChanged(),
    ).subscribe(() => {
      this.breadcrumbs = this.createBreadcrumbs(this.activatedRoute.root);
    });
  }

  private initializeBreadcrumbs() {
    // Get the current URL
    const currentUrl = this.router.url;
    const urlSegments = currentUrl.split('/').filter(segment => segment);
    
    // Build the breadcrumbs based on the current URL
    let currentPath = '';
    this.breadcrumbs = urlSegments.map(segment => {
      currentPath += `/${segment}`;
      
      // Try to get the route data for this segment
      const route = this.findRouteForPath(currentPath);
      const label = route?.data?.['breadcrumb'] || this.generateLabel(segment);
      
      return {
        label: label.replace("%2F", "/"),
        url: currentPath
      };
    });
  }

  private findRouteForPath(path: string) {
    // Normalize the path
    const normalizedPath = path.startsWith('/') ? path.substring(1) : path;
    
    // Recursively search through the router config to find the matching route
    const findRoute = (routes: any[], searchPath: string): any => {
      for (const route of routes) {
        if (route.path === searchPath) {
          return route;
        }
        if (route.children) {
          const childRoute = findRoute(route.children, searchPath);
          if (childRoute) {
            return childRoute;
          }
        }
      }
      return null;
    };

    return findRoute(this.router.config, normalizedPath);
  }

  private createBreadcrumbs(route: ActivatedRoute, url: string = '', breadcrumbs: Breadcrumb[] = []): Breadcrumb[] {
    const children: ActivatedRoute[] = route.children;

    if (children.length === 0) {
      return breadcrumbs;
    }

    for (const child of children) {
      const routeURL: string = child.snapshot.url.map(segment => segment.path).join('/');
      if (routeURL !== '') {
        url += `/${routeURL}`;
      }

      const label = child.snapshot.data['breadcrumb'] || this.generateLabel(routeURL);
      if (label) {
        breadcrumbs.push({ label, url });
      }

      return this.createBreadcrumbs(child, url, breadcrumbs);
    }

    return breadcrumbs;
  }

  private generateLabel(path: string): string {
    // Convert path to readable label (e.g., 'user-profile' -> 'User Profile')
    if (!path) return '';
    return path;
  }
}
